import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { ImageUpload } from "../../components/admin/ImageUpload";

export default function AdminAppearance() {
  const { reloadSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => setForm(data))
      .catch(() => {});
  }, []);

  if (!form) {
    return <p className="text-[#536D61]">Φόρτωση...</p>;
  }

  const set = (path) => (e) => {
    const val = e?.target ? e.target.value : e;

    setForm((f) => {
      const next = { ...f };
      const keys = path.split(".");
      let obj = next;

      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...(obj[keys[i]] || {}) };
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] = val;

      return next;
    });
  };

  const save = async () => {
    setSaving(true);

    try {
      const payload = {
        ...form,
        free_shipping_threshold:
          parseFloat(form.free_shipping_threshold) || 0,
        shipping_cost:
          parseFloat(form.shipping_cost) || 0,
      };

      await api.put("/settings", payload);
      await reloadSettings();

      toast.success("Οι ρυθμίσεις αποθηκεύτηκαν");
    } catch {
      toast.error("Σφάλμα αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2.5 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  const Section = ({ title, children }) => (
    <div className="bg-white border border-[#E5E9E4] rounded-sm p-6">
      <h2 className="font-semibold text-[#1E3A2F] mb-4">
        {title}
      </h2>

      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
        {label}
      </label>

      {children}
    </div>
  );

  const Toggle = ({ path, label, checked = true }) => (
    <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => set(path)(e.target.checked)}
        className="accent-[#1E3A2F]"
      />

      {label}
    </label>
  );

  return (
    <div className="space-y-6 pb-24">

      {/* =========================================================
          TITLE
      ========================================================= */}

      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-3xl text-[#1E3A2F]">
          Εμφάνιση & Πληροφορίες
        </h1>
      </div>

      {/* =========================================================
          ΓΕΝΙΚΑ
      ========================================================= */}

      <Section title="Γενικά">

        <Field label="Όνομα καταστήματος">
          <input
            data-testid="settings-shop-name"
            value={form.shop_name || ""}
            onChange={set("shop_name")}
            className={inputCls}
          />
        </Field>

        <Field label="Μπάρα ανακοίνωσης (πάνω-πάνω)">
          <input
            value={form.announcement || ""}
            onChange={set("announcement")}
            className={inputCls}
          />
        </Field>

        <ImageUpload
          value={form.logo}
          onChange={set("logo")}
          label="Λογότυπο"
          testid="setting-logo-upload"
        />

      </Section>

      {/* =========================================================
          HERO
      ========================================================= */}

      <Section title="Κεντρική εικόνα (Hero)">

        <Toggle
          path="hero.enabled"
          label="Εμφάνιση Hero στην αρχική"
          checked={form.hero?.enabled !== false}
        />

        <Field label="Τίτλος">
          <input
            data-testid="setting-hero-title"
            value={form.hero?.title || ""}
            onChange={set("hero.title")}
            className={inputCls}
          />
        </Field>

        <Field label="Υπότιτλος">
          <textarea
            value={form.hero?.subtitle || ""}
            onChange={set("hero.subtitle")}
            rows={3}
            className={inputCls}
          />
        </Field>

        <Field label="Κείμενο κουμπιού">
          <input
            value={form.hero?.cta_text || ""}
            onChange={set("hero.cta_text")}
            className={inputCls}
          />
        </Field>

        <Field label="Σύνδεσμος κουμπιού">
          <input
            value={form.hero?.cta_link || ""}
            onChange={set("hero.cta_link")}
            placeholder="/shop"
            className={inputCls}
          />
        </Field>

        <ImageUpload
          value={form.hero?.image}
          onChange={set("hero.image")}
          label="Εικόνα Hero Desktop"
          testid="setting-hero-image"
        />

        <ImageUpload
          value={form.hero?.mobile_image}
          onChange={set("hero.mobile_image")}
          label="Εικόνα Hero Mobile (προαιρετική)"
          testid="setting-hero-mobile-image"
        />

      </Section>

      {/* =========================================================
          ΔΟΜΗ ΑΡΧΙΚΗΣ
      ========================================================= */}

      <Section title="Δομή αρχικής σελίδας">

        <p className="text-sm text-[#687B73]">
          Από εδώ επιλέγεις ποιες βασικές ενότητες θα εμφανίζονται
          στην αρχική σελίδα και πόσα στοιχεία θα προβάλλονται.
        </p>

        {/* =====================================================
            ΚΑΤΗΓΟΡΙΕΣ
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.categories.enabled"
            label="Εμφάνιση κατηγοριών"
            checked={
              form.homepage?.categories?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.categories?.title ||
                "Κατηγορίες προϊόντων"
              }
              onChange={set("homepage.categories.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Πλήθος κατηγοριών">
            <input
              type="number"
              min="1"
              max="20"
              value={
                form.homepage?.categories?.count ?? 8
              }
              onChange={set("homepage.categories.count")}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            ΠΡΟΣΦΟΡΕΣ
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.offers.enabled"
            label="Εμφάνιση Προσφορών"
            checked={
              form.homepage?.offers?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.offers?.title ||
                "Προσφορές"
              }
              onChange={set("homepage.offers.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Πλήθος προϊόντων">
            <input
              type="number"
              min="1"
              max="20"
              value={
                form.homepage?.offers?.count ?? 8
              }
              onChange={set("homepage.offers.count")}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            ΝΕΑ ΠΡΟΪΟΝΤΑ
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.new_products.enabled"
            label="Εμφάνιση Νέων προϊόντων"
            checked={
              form.homepage?.new_products?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.new_products?.title ||
                "Νέα προϊόντα"
              }
              onChange={set("homepage.new_products.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Πλήθος προϊόντων">
            <input
              type="number"
              min="1"
              max="20"
              value={
                form.homepage?.new_products?.count ?? 8
              }
              onChange={set("homepage.new_products.count")}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            BEST SELLERS
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.best_sellers.enabled"
            label="Εμφάνιση Best Sellers"
            checked={
              form.homepage?.best_sellers?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.best_sellers?.title ||
                "Best Sellers"
              }
              onChange={set("homepage.best_sellers.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Πλήθος προϊόντων">
            <input
              type="number"
              min="1"
              max="20"
              value={
                form.homepage?.best_sellers?.count ?? 8
              }
              onChange={set("homepage.best_sellers.count")}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            GIFT MOMENTS
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.gift_moments.enabled"
            label="Εμφάνιση Gift Moments"
            checked={
              form.homepage?.gift_moments?.enabled !== false
            }
          />

          <Field label="Τίτλος">
            <input
              value={
                form.homepage?.gift_moments?.title ||
                "Gift Moments"
              }
              onChange={set("homepage.gift_moments.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Υπότιτλος">
            <textarea
              value={
                form.homepage?.gift_moments?.subtitle || ""
              }
              onChange={set("homepage.gift_moments.subtitle")}
              rows={3}
              className={inputCls}
            />
          </Field>

          <Field label="Σύνδεσμος">
            <input
              value={
                form.homepage?.gift_moments?.link ||
                "/category/gift-moments"
              }
              onChange={set("homepage.gift_moments.link")}
              placeholder="/category/gift-moments"
              className={inputCls}
            />
          </Field>

          <ImageUpload
            value={form.homepage?.gift_moments?.image}
            onChange={set("homepage.gift_moments.image")}
            label="Εικόνα Gift Moments"
            testid="setting-gift-moments-image"
          />

        </div>

        {/* =====================================================
            ΘΕΜΑΤΙΚΕΣ ΑΝΑΓΚΕΣ
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.thematic_needs.enabled"
            label="Εμφάνιση Θεματικών αναγκών"
            checked={
              form.homepage?.thematic_needs?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.thematic_needs?.title ||
                "Φροντίδα για κάθε ανάγκη"
              }
              onChange={set("homepage.thematic_needs.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Υπότιτλος">
            <textarea
              value={
                form.homepage?.thematic_needs?.subtitle || ""
              }
              onChange={set("homepage.thematic_needs.subtitle")}
              rows={2}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            BRANDS
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.brands.enabled"
            label="Εμφάνιση Brands"
            checked={
              form.homepage?.brands?.enabled !== false
            }
          />

          <Field label="Τίτλος Brands">
            <input
              value={
                form.homepage?.brands?.title ||
                form.brands_title ||
                "Συνεργαζόμαστε με κορυφαίες μάρκες"
              }
              onChange={set("homepage.brands.title")}
              className={inputCls}
            />
          </Field>

        </div>

        {/* =====================================================
            ΓΙΑΤΙ ΝΑ ΜΑΣ ΕΠΙΛΕΞΕΤΕ
        ===================================================== */}

        <div className="border border-[#E5E9E4] rounded-sm p-4 space-y-4">

          <Toggle
            path="homepage.why_choose_us.enabled"
            label="Εμφάνιση «Γιατί να μας επιλέξετε»"
            checked={
              form.homepage?.why_choose_us?.enabled !== false
            }
          />

          <Field label="Τίτλος ενότητας">
            <input
              value={
                form.homepage?.why_choose_us?.title ||
                "Γιατί να μας επιλέξετε"
              }
              onChange={set("homepage.why_choose_us.title")}
              className={inputCls}
            />
          </Field>

          <Field label="Υπότιτλος">
            <textarea
              value={
                form.homepage?.why_choose_us?.subtitle || ""
              }
              onChange={set("homepage.why_choose_us.subtitle")}
              rows={2}
              className={inputCls}
            />
          </Field>

        </div>

      </Section>

      {/* =========================================================
          ΤΟ ΦΑΡΜΑΚΕΙΟ ΜΑΣ
      ========================================================= */}

      <Section title="Ενότητα «Το φαρμακείο μας»">

        <Toggle
          path="store_section.enabled"
          label="Εμφάνιση ενότητας στην αρχική"
          checked={
            form.store_section?.enabled !== false
          }
        />

        <Field label="Τίτλος">
          <input
            data-testid="setting-store-title"
            value={form.store_section?.title || ""}
            onChange={set("store_section.title")}
            className={inputCls}
          />
        </Field>

        <Field label="Κείμενο / πληροφορίες">
          <textarea
            data-testid="setting-store-text"
            value={form.store_section?.text || ""}
            onChange={set("store_section.text")}
            rows={5}
            className={inputCls}
          />
        </Field>

        <ImageUpload
          value={form.store_section?.image}
          onChange={set("store_section.image")}
          label="Φωτογραφία φαρμακείου"
          testid="setting-store-image"
        />

      </Section>

      {/* =========================================================
          ΕΠΙΚΟΙΝΩΝΙΑ
      ========================================================= */}

      <Section title="Στοιχεία επικοινωνίας">

        <div className="grid sm:grid-cols-2 gap-4">

          <Field label="Τηλέφωνο">
            <input
              data-testid="setting-phone"
              value={form.contact?.phone || ""}
              onChange={set("contact.phone")}
              className={inputCls}
            />
          </Field>

          <Field label="Email">
            <input
              value={form.contact?.email || ""}
              onChange={set("contact.email")}
              className={inputCls}
            />
          </Field>

          <Field label="Διεύθυνση">
            <input
              value={form.contact?.address || ""}
              onChange={set("contact.address")}
              className={inputCls}
            />
          </Field>

          <Field label="Ωράριο">
            <input
              value={form.contact?.hours || ""}
              onChange={set("contact.hours")}
              className={inputCls}
            />
          </Field>

        </div>

        <div className="grid sm:grid-cols-2 gap-4">

          <Field label="Facebook URL">
            <input
              value={form.social?.facebook || ""}
              onChange={set("social.facebook")}
              className={inputCls}
            />
          </Field>

          <Field label="Instagram URL">
            <input
              value={form.social?.instagram || ""}
              onChange={set("social.instagram")}
              className={inputCls}
            />
          </Field>

        </div>

      </Section>

      {/* =========================================================
          ΑΠΟΣΤΟΛΗ
      ========================================================= */}

      <Section title="Αποστολή">

        <Field label="Κείμενο πληροφοριών αποστολής">
          <textarea
            value={form.shipping_info || ""}
            onChange={set("shipping_info")}
            rows={2}
            className={inputCls}
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">

          <Field label="Όριο δωρεάν αποστολής (€)">
            <input
              type="number"
              step="0.01"
              value={form.free_shipping_threshold ?? ""}
              onChange={set("free_shipping_threshold")}
              className={inputCls}
            />
          </Field>

          <Field label="Κόστος αποστολής (€)">
            <input
              type="number"
              step="0.01"
              value={form.shipping_cost ?? ""}
              onChange={set("shipping_cost")}
              className={inputCls}
            />
          </Field>

        </div>

      </Section>

      {/* =========================================================
          ΠΛΗΡΩΜΕΣ
      ========================================================= */}

      <Section title="Τρόποι πληρωμής">

        <Toggle
          path="payment.cod_enabled"
          label="Αντικαταβολή"
          checked={!!form.payment?.cod_enabled}
        />

        <Toggle
          path="payment.stripe_enabled"
          label="Κάρτα (Stripe)"
          checked={!!form.payment?.stripe_enabled}
        />

        <Toggle
          path="payment.bank_enabled"
          label="Κατάθεση σε τραπεζικό λογαριασμό"
          checked={!!form.payment?.bank_enabled}
        />

        {form.payment?.bank_enabled && (
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#E5E9E4]">

            <Field label="Τράπεζα">
              <input
                value={
                  form.payment?.bank_details?.bank_name || ""
                }
                onChange={set(
                  "payment.bank_details.bank_name"
                )}
                className={inputCls}
              />
            </Field>

            <Field label="Δικαιούχος">
              <input
                value={
                  form.payment?.bank_details?.account_name || ""
                }
                onChange={set(
                  "payment.bank_details.account_name"
                )}
                className={inputCls}
              />
            </Field>

            <Field label="IBAN">
              <input
                data-testid="setting-iban"
                value={
                  form.payment?.bank_details?.iban || ""
                }
                onChange={set(
                  "payment.bank_details.iban"
                )}
                className={inputCls}
              />
            </Field>

            <Field label="Σημειώσεις (προαιρετικά)">
              <input
                value={
                  form.payment?.bank_details?.notes || ""
                }
                onChange={set(
                  "payment.bank_details.notes"
                )}
                className={inputCls}
              />
            </Field>

          </div>
        )}

      </Section>

      {/* =========================================================
          NEWSLETTER & ΜΑΡΚΕΣ
      ========================================================= */}

      <Section title="Newsletter & Μάρκες">

        <Toggle
          path="newsletter.enabled"
          label="Εμφάνιση ενότητας newsletter στην αρχική"
          checked={
            form.newsletter?.enabled !== false
          }
        />

        <Field label="Τίτλος newsletter">
          <input
            value={form.newsletter?.title || ""}
            onChange={set("newsletter.title")}
            className={inputCls}
          />
        </Field>

        <Field label="Υπότιτλος newsletter">
          <input
            value={form.newsletter?.subtitle || ""}
            onChange={set("newsletter.subtitle")}
            className={inputCls}
          />
        </Field>

        <Field label="Τίτλος ενότητας μαρκών">
          <input
            value={form.brands_title || ""}
            onChange={set("brands_title")}
            className={inputCls}
          />
        </Field>

      </Section>

      {/* =========================================================
          FOOTER
      ========================================================= */}

      <Section title="Υποσέλιδο (Footer)">

        <Field label="Κείμενο/περιγραφή κάτω από το λογότυπο">
          <textarea
            data-testid="setting-footer-about"
            value={form.footer_about || ""}
            onChange={set("footer_about")}
            rows={3}
            className={inputCls}
          />
        </Field>

        <Field label="Κείμενο copyright (αφήστε κενό για αυτόματο)">
          <input
            value={form.copyright || ""}
            onChange={set("copyright")}
            className={inputCls}
          />
        </Field>

      </Section>

      {/* =========================================================
          SAVE
      ========================================================= */}

      <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white border-t border-[#E5E9E4] px-4 sm:px-8 py-4 flex justify-end">

        <button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-button"
          className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-6 py-3 rounded-sm font-medium hover:bg-[#111C18] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />

          {saving
            ? "Αποθήκευση..."
            : "Αποθήκευση αλλαγών"}
        </button>

      </div>

    </div>
  );
}